import type { Eip1193Provider } from "./eip1193";

export const TRADE_DIAGNOSTICS_KEY = "merklewood.trade.diagnostics";
type Fields = {
  workId?: string | undefined; spanId?: number; requestedBlock?: string; transport?: string; transportId?: string; endpointOrigin?: string; method?: string;
  methods?: string[]; callCount?: number; blockNumber?: string; blockHash?: string;
  outcome?: "ok" | "error" | "timeout" | "cancelled"; durationMs?: number;
  round?: number; found?: boolean; enabled?: boolean; allowanceEnough?: boolean;
  receiptBlockNumber?: string | undefined; receiptBlockHash?: string | undefined;
};
type ProviderInfo = Pick<Fields, "transport" | "transportId" | "endpointOrigin" | "workId"> & { operationId?: string };
const providers = new WeakMap<Eip1193Provider, ProviderInfo>();
let sequence = 0;

export function tradeDiagnosticsEnabled(): boolean {
  try { return sessionStorage.getItem(TRADE_DIAGNOSTICS_KEY) === "1"; } catch { return false; }
}

/** Console only; never include payloads, accounts, hashes of transactions, raw errors or URL paths/query. */
export function tradeDiagnostic(operationId: string | undefined, stage: string, fields: Fields = {}): void {
  try {
    if (!tradeDiagnosticsEnabled()) return;
    console.debug("[trade-latency]", { operationId, stage, atMs: performance.now(),
      visibility: typeof document === "undefined" ? "unknown" : document.visibilityState, ...fields });
  } catch { /* Diagnostics must never affect transaction handling. */ }
}

export function diagnosticOutcome(error: unknown): "error" | "timeout" | "cancelled" {
  if (error instanceof Error && /timeout/i.test(error.message)) return "timeout";
  if (error instanceof Error && error.name === "AbortError") return "cancelled";
  return "error";
}

/** Classify the first abort of the request controller, even if the caller aborts later. */
export function diagnosticTransportOutcome(error: unknown, caller: AbortSignal | undefined, request: AbortSignal) {
  if (request.aborted) return caller?.aborted && request.reason === caller.reason ? "cancelled" : "timeout";
  return caller?.aborted ? "cancelled" : diagnosticOutcome(error);
}

export function tradeDiagnosticWorkId(): string | undefined {
  return tradeDiagnosticsEnabled() ? `work-${++sequence}` : undefined;
}

const noopSpan: (outcome?: Fields["outcome"], extra?: Fields) => void = () => {};

export function tradeSpan(operationId: string | undefined, stage: string, fields: Fields = {}) {
  if (!tradeDiagnosticsEnabled()) return noopSpan;
  fields = { ...fields, spanId: ++sequence };
  const started = performance.now();
  tradeDiagnostic(operationId, `${stage}:start`, fields);
  let ended = false;
  return (outcome: Fields["outcome"] = "ok", extra: Fields = {}) => {
    if (ended) return;
    ended = true;
    tradeDiagnostic(operationId, `${stage}:end`, { ...fields, ...extra, outcome, durationMs: performance.now() - started });
  };
}

export function registerTradeTransport(provider: Eip1193Provider, transport: string, url: URL): Eip1193Provider {
  providers.set(provider, { transport, transportId: `transport-${++sequence}`, endpointOrigin: url.origin });
  return provider;
}
export function tradeProviderInfo(provider: Eip1193Provider): ProviderInfo {
  return providers.get(provider) ?? { transport: "eip1193" };
}
/** Forward exactly once, preserving the provider promise and synchronous throws. */
export function diagnosticTradeProvider(provider: Eip1193Provider, operationId: string | undefined, source = provider, workId?: string): Eip1193Provider {
  if (!tradeDiagnosticsEnabled()) return provider;
  const scoped: Eip1193Provider = { request: args => provider.request(args) };
  providers.set(scoped, { ...tradeProviderInfo(source), ...(operationId === undefined ? {} : { operationId }),
    ...(workId === undefined ? {} : { workId }) });
  return scoped;
}

export function diagnosticBlock(value: unknown): Pick<Fields, "blockNumber" | "blockHash"> {
  if (!value || typeof value !== "object") return {};
  const item = value as Record<string, unknown>;
  const number = item.blockNumber ?? item.number, hash = item.blockHash ?? item.hash;
  return { ...(typeof number === "string" && /^0x[0-9a-f]+$/i.test(number) ? { blockNumber: number } : {}),
    ...(typeof hash === "string" && /^0x[0-9a-f]{64}$/i.test(hash) ? { blockHash: hash } : {}) };
}
